import React from 'react';

// Soft two-tone pastel gradients with a matching dark, readable text color —
// enough variety that a full grid of cards doesn't repeat the same couple of colors.
const AVATAR_PALETTE = [
    { bg: 'linear-gradient(135deg, #eef2ff, #e0e7ff)', color: '#4338ca' },
    { bg: 'linear-gradient(135deg, #fce7f3, #fecdd3)', color: '#9f1239' },
    { bg: 'linear-gradient(135deg, #cffafe, #dbeafe)', color: '#0369a1' },
    { bg: 'linear-gradient(135deg, #fef3c7, #fed7aa)', color: '#92400e' },
    { bg: 'linear-gradient(135deg, #d1fae5, #ccfbf1)', color: '#065f46' },
    { bg: 'linear-gradient(135deg, #ede9fe, #fae8ff)', color: '#7c3aed' },
    { bg: 'linear-gradient(135deg, #fee2e2, #fef3c7)', color: '#b45309' },
    { bg: 'linear-gradient(135deg, #e0f2fe, #e0e7ff)', color: '#1d4ed8' },
    { bg: 'linear-gradient(135deg, #ccfbf1, #dcfce7)', color: '#0f766e' },
    { bg: 'linear-gradient(135deg, #ffe4e6, #fce7f3)', color: '#be123c' },
    { bg: 'linear-gradient(135deg, #ede9fe, #e0e7ff)', color: '#5b21b6' },
    { bg: 'linear-gradient(135deg, #d1fae5, #a7f3d0)', color: '#047857' },
];

const hashString = (str: string): number => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash |= 0;
    }
    return Math.abs(hash);
};

export const getInitials = (firstName?: string, lastName?: string, fallbackName?: string): string => {
    const first = (firstName ?? '').trim();
    const last = (lastName ?? '').trim();
    if (first || last) {
        return (first.charAt(0) + last.charAt(0)).toUpperCase() || first.slice(0, 2).toUpperCase();
    }
    const parts = (fallbackName ?? '').trim().split(/\s+/).filter(Boolean);
    return parts.length ? parts.slice(0, 2).map((p) => p.charAt(0)).join('').toUpperCase() : '?';
};

export const hasProfilePicture = (src?: string | null): boolean =>
    !!src && src !== 'null' && String(src).trim() !== '';

interface InitialsAvatarProps {
    firstName?: string;
    lastName?: string;
    name?: string;
    /** Pixel size. Ignored when `fill` is true. Default 60 to match directory cards. */
    size?: number;
    /** Fill the parent circle (profile header is 110px / 80px on mobile). */
    fill?: boolean;
}

/**
 * Meeting-app-style fallback avatar (a la Zoom/Meet/Teams) — a gradient circle
 * with the person's initials, shown in place of a photo when there isn't one.
 *
 * Sized with a fixed 60px (matching `.alumni_img`'s own fixed 60x60 circle)
 * rather than width/height: 100% — the immediate wrappers around this control
 * (`<a class="d-flex">` > `<i class="position-relative">`) are auto-sized by
 * their own content, so a percentage size here wouldn't reliably resolve
 * against the outer 60x60 circle, leaving its grey background visible
 * around the edges. A fixed size guarantees it fully covers that circle.
 */
const InitialsAvatar = ({ firstName, lastName, name, size = 60, fill = false }: InitialsAvatarProps) => {
    const initials = getInitials(firstName, lastName, name);
    const palette = AVATAR_PALETTE[hashString(`${firstName ?? ''}${lastName ?? ''}${name ?? ''}` || initials) % AVATAR_PALETTE.length];

    return (
        <div
            className="d-flex align-items-center justify-content-center fw-bold"
            style={{
                width: fill ? '100%' : size,
                height: fill ? '100%' : size,
                minWidth: fill ? undefined : size,
                minHeight: fill ? undefined : size,
                borderRadius: '50%',
                overflow: 'hidden',
                background: palette.bg,
                color: palette.color,
                fontSize: fill ? '1.8rem' : `${Math.max(11, Math.round(size * 0.307))}px`,
                lineHeight: 1,
                letterSpacing: '0.5px',
                boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.06)',
            }}
        >
            {initials}
        </div>
    );
};

export default InitialsAvatar;
